import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import FAQAccordion from '@/components/base/FAQAccordion';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqs } from '@/mocks/faqs';
import { useState } from 'react';

const HomePage = () => {
  const featuredArticles = articles.filter((a) => a.featured);
  const allArticles = articles.slice(0, 6);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9srbfdlbrt1qcgaj8n0', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
        if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后重试。');
          setFormStatus('error');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查连接后重试。');
      setFormStatus('error');
    }
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        name: '深度解析 - 人妻猎人现象全剖析',
        url: 'https://www.example.com/',
        description: '深度解析人妻猎人这一都市猎艳文化现象的起源、心理动机与社会影响，提供婚姻危机预防指南。',
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'WebPage',
        name: '人妻猎人深度解析 - 首页',
        url: 'https://www.example.com/',
        isPartOf: { '@type': 'WebSite', name: '深度解析', url: 'https://www.example.com/' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <Navbar transparent />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20warm%20minimalist%20landscape%20with%20soft%20amber%20and%20terracotta%20gradient%2C%20flowing%20organic%20shapes%2C%20atmospheric%20mist%2C%20contemporary%20art%20style%2C%20emotional%20depth%2C%20serene%20yet%20powerful%20composition&width=1800&height=900&seq=hero-main&orientation=landscape"
            alt="人妻猎人现象深度解析"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-3xl mx-auto">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4 leading-tight">
              深度解析都市猎艳文化现象
            </h1>
            <p className="text-base md:text-lg text-background-200 mb-8 leading-relaxed max-w-2xl mx-auto">
              从社会学、心理学与伦理学多重视角，客观剖析"人妻猎人"现象的起源、动机、影响与争议，帮助读者全面理解背后的情感与伦理议题。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/xianxiang-jiexi"
                className="px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                开始探索
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 border border-background-300 text-background-200 font-medium rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                探索五大核心维度
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
                从多个角度全面解读这一复杂的都市文化现象
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 p-5 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-300 text-center cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1 whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-3">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  精选深度文章
                </h2>
                <p className="text-sm text-foreground-600 mt-1">
                  编辑推荐的深度分析与研究
                </p>
              </div>
              <Link
                to="/category/xianxiang-jiexi"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8">
              最新文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {allArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题解答
              </h2>
              <p className="text-sm text-foreground-600">
                关于人妻猎人现象与婚姻关系的常见疑问
              </p>
            </div>
            <FAQAccordion faqs={faqs} />
          </div>
        </section>

        {/* Newsletter */}
        <section className="w-full py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-mail-line text-2xl text-primary-600"></i>
            </div>
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-2">
              订阅深度分析
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              获取最新文章、深度分析和婚姻关系维护指南，直接发送到您的邮箱。
            </p>
            {formStatus === 'success' ? (
              <div className="py-4 px-6 bg-primary-50 border border-primary-200 rounded-lg">
                <p className="text-primary-700 font-medium text-sm">
                  感谢订阅！我们会将最新内容发送到您的邮箱。
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleNewsletterSubmit}
                data-readdy-form
                className="flex flex-col sm:flex-row gap-3"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="输入您的邮箱地址"
                  required
                  className="flex-1 px-4 py-3 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="spam-field"
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  订阅
                </button>
              </form>
            )}
            {formStatus === 'error' && (
              <p className="text-xs text-red-600 mt-2">{formError}</p>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default HomePage;